import { useCallback, useRef, useState } from 'react';
import { Auth } from '~/amplify';
import {
  useGetSessionHydrationLazyQuery,
  useUserProfileSyncMutation,
} from '~/graphql/types';
import { reporter } from '~/hooks/useErrorReporter';
import { sleep } from '~/util';

type AllowedMFAPreferenceUpdate =
  | { totp: 'ENABLED'; sms?: never }
  | { totp: 'DISABLED'; sms: 'DISABLED' };

type UseUpdateMFAPreferenceAndSyncUserReturn = {
  loading: boolean;

  updateMFAPreferenceWithSync: ({
    accountId,
    update,
  }: {
    accountId: string;
    update: AllowedMFAPreferenceUpdate;
  }) => Promise<void>;
};

const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 2000;

/**
 * Updates the MFA preference and syncs the user profile
 */
const useUpdateMFAPreferenceAndSyncUser =
  (): UseUpdateMFAPreferenceAndSyncUserReturn => {
    const [loading, setLoading] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const [getSessionHydration] = useGetSessionHydrationLazyQuery();

    const [userProfileSync] = useUserProfileSyncMutation({
      onError: error => {
        reporter.captureException(
          new Error(`MFASetupTOTP: Failed to userProfileSync: ${error}`),
          'debug',
        );
      },
    });

    const updateMFAPreferenceWithSync = useCallback(
      async ({
        accountId,
        update,
      }: {
        accountId: string;
        update: AllowedMFAPreferenceUpdate;
      }) => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);

        try {
          await Auth.updateMFAPreference(update);
          await userProfileSync({ variables: { accountId } });

          let didMatch = false;
          for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
            if (controller.signal.aborted) return;

            const { data: sessionHydration } = await getSessionHydration({
              variables: { accountId },
              fetchPolicy: 'network-only',
            });

            const currentUser = sessionHydration?.getSessionHydration.me;

            if (!currentUser) {
              throw new Error('User not found');
            }

            const matches =
              (update.totp === 'ENABLED' && currentUser.hasMfa) ||
              (update.totp === 'DISABLED' && !currentUser.hasMfa);

            if (matches) {
              didMatch = true;
              break;
            }

            if (attempt < MAX_RETRIES - 1) {
              await userProfileSync({ variables: { accountId } });
              await sleep(RETRY_DELAY_MS);
            }
          }

          if (!didMatch) {
            throw new Error(
              `Timed out waiting for MFA state to sync after ${MAX_RETRIES} attempts`,
            );
          }
        } catch (err) {
          if (controller.signal.aborted) return;

          reporter.captureException(
            err instanceof Error ? err : new Error(String(err)),
            'error',
          );
          throw err;
        } finally {
          if (!controller.signal.aborted) {
            setLoading(false);
          }
        }
      },
      [getSessionHydration, userProfileSync],
    );

    return { loading, updateMFAPreferenceWithSync };
  };

export default useUpdateMFAPreferenceAndSyncUser;
